/**
 * useKanbanBulkActions
 * 
 * Hook para gerenciar ações em massa nas negociações.
 * Permite mover etapa, mudar atendente e excluir múltiplos leads.
 * 
 * Princípios aplicados:
 * - Single Responsibility: Apenas ações em massa
 * - Separation of Concerns: Lógica separada do componente
 */

import { useState, useMemo, useCallback } from 'react';
import { useLeads } from '../../../contexts/leads/LeadsContext';
import { Lead, FunnelStage, CrmUser } from '../../../types';
import { ArrowRightIcon, UserIcon, TrashIcon } from '../../../components/icons';
import { createModuleLogger } from '../../../utils/logger';
import { BulkAction } from '../../../components/shared/BulkActionsBar';

const log = createModuleLogger('[KanbanBulk]');

interface UseKanbanBulkActionsProps {
    getSelectedItems: () => Lead[];
    clearSelection: () => void;
    funnelStages: FunnelStage[];
    users: CrmUser[];
}

export function useKanbanBulkActions({
    getSelectedItems,
    clearSelection,
    funnelStages,
    users
}: UseKanbanBulkActionsProps) {
    const { saveLead, deleteLead } = useLeads();
    const [isBulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

    const handleBulkMoveStage = useCallback(async (stageId: string) => {
        log.log('🔄 handleBulkMoveStage called with stageId:', stageId);
        const selectedLeads = getSelectedItems();
        log.log('📋 Selected leads:', selectedLeads.length);

        if (selectedLeads.length === 0) return;

        try {
            for (const lead of selectedLeads) {
                log.log('💾 Saving lead:', lead.id, 'to stage:', stageId);
                await saveLead({ ...lead, stage: stageId });
            }
            clearSelection();
        } catch (error) {
            log.error('Error moving leads:', error);
        }
    }, [getSelectedItems, clearSelection, saveLead]);

    const handleBulkChangeAgent = useCallback(async (agentId: string) => {
        log.log('👤 handleBulkChangeAgent called with agentId:', agentId);
        const selectedLeads = getSelectedItems();
        log.log('📋 Selected leads:', selectedLeads.length);

        if (selectedLeads.length === 0) return;

        try {
            for (const lead of selectedLeads) {
                log.log('💾 Saving lead:', lead.id, 'with agent:', agentId || 'null');
                await saveLead({ ...lead, agent: agentId || null });
            }
            clearSelection();
        } catch (error) {
            log.error('Error changing agent:', error);
        }
    }, [getSelectedItems, clearSelection, saveLead]);

    const handleBulkDelete = useCallback(async () => {
        const selectedLeads = getSelectedItems();
        try {
            for (const lead of selectedLeads) {
                await deleteLead(lead.id);
            }
            clearSelection();
            setBulkDeleteModalOpen(false);
        } catch (error) {
            log.error('Error deleting leads:', error);
        }
    }, [getSelectedItems, clearSelection, deleteLead]);

    const bulkActions: BulkAction[] = useMemo(() => [
        {
            label: 'Mover Etapa',
            icon: ArrowRightIcon,
            type: 'dropdown' as const,
            variant: 'primary' as const,
            options: funnelStages.map(stage => ({
                id: stage.id,
                label: stage.name
            })),
            onSelectOption: handleBulkMoveStage
        },
        {
            label: 'Mudar Atendente',
            icon: UserIcon,
            type: 'dropdown' as const,
            variant: 'primary' as const,
            options: [
                { id: '', label: 'Ninguém' },
                ...users.map(user => ({
                    id: user.id,
                    label: user.name
                }))
            ],
            onSelectOption: handleBulkChangeAgent
        },
        {
            label: 'Excluir',
            icon: TrashIcon,
            type: 'button' as const,
            onClick: () => setBulkDeleteModalOpen(true),
            variant: 'danger' as const
        }
    ], [funnelStages, users, handleBulkMoveStage, handleBulkChangeAgent]);

    return {
        bulkActions,
        isBulkDeleteModalOpen,
        setBulkDeleteModalOpen,
        handleBulkDelete
    };
}
